/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { FiCpu, FiX, FiAlertTriangle, FiCheckCircle, FiEdit3 } from "react-icons/fi";
import {
  useProposerEcriturePieceMutation,
  useValiderEcriturePieceMutation,
} from "actions/pieces/mutations";
import TableLoader from "components/Loader/TableLoader";

const JOURNAL_LABELS = {
  OD: "Opérations diverses",
  ACHATS: "Journal des achats",
  VENTES: "Journal des ventes",
  BANQUE: "Journal de banque",
  CAISSE: "Journal de caisse",
  SALAIRES: "Journal des salaires",
  FISCALITE: "Journal des déclarations fiscales",
};

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

const EcritureProposeeModal = ({ open, onClose, piece, dossier_id, onValidated }) => {
  const [proposition, setProposition] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [libelle, setLibelle] = useState("");
  const [dateEcriture, setDateEcriture] = useState("");
  const [journalType, setJournalType] = useState("OD");
  const [avertissements, setAvertissements] = useState([]);
  const [confiance, setConfiance] = useState(0);
  const [dejaComptabilisee, setDejaComptabilisee] = useState(false);

  const [proposer, { isLoading: isProposing }] = useProposerEcriturePieceMutation();
  const [valider, { isLoading: isValidating }] = useValiderEcriturePieceMutation();

  // Charger la proposition a l'ouverture de la modale
  useEffect(() => {
    if (!open || !piece?.id || !dossier_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await proposer({ piece_id: piece.id, dossier_id }).unwrap();
        if (cancelled) return;
        const prop = res?.proposition;
        if (!prop) {
          toast.error("Aucune proposition retournée par le moteur.");
          onClose();
          return;
        }
        setProposition(prop);
        setLignes(prop.lignes || []);
        setLibelle(prop.libelle || "");
        setDateEcriture(prop.date_ecriture || "");
        setJournalType(prop.journal_type || "OD");
        setAvertissements(prop.avertissements || []);
        setConfiance(prop.confiance || 0);
        setDejaComptabilisee(!!prop.deja_comptabilisee);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.data?.message || "Impossible de proposer une écriture.");
          onClose();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, piece, dossier_id, proposer, onClose]);

  // Reinitialiser quand on ferme
  useEffect(() => {
    if (!open) {
      setProposition(null);
      setLignes([]);
      setLibelle("");
      setDateEcriture("");
      setJournalType("OD");
      setAvertissements([]);
      setConfiance(0);
      setDejaComptabilisee(false);
    }
  }, [open]);

  const totalDebit = useMemo(
    () => lignes.filter(l => l.sens === "debit").reduce((s, l) => s + (Number(l.montant) || 0), 0),
    [lignes]
  );
  const totalCredit = useMemo(
    () => lignes.filter(l => l.sens === "credit").reduce((s, l) => s + (Number(l.montant) || 0), 0),
    [lignes]
  );
  const equilibre = totalDebit === totalCredit && totalDebit > 0;

  const updateLigne = (index, field, value) => {
    setLignes(prev => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  };

  const handleValider = async () => {
    if (!equilibre) {
      toast.error("Écriture non équilibrée : débit ≠ crédit.");
      return;
    }
    try {
      await valider({
        piece_id: piece.id,
        dossier_id,
        libelle,
        date_ecriture: dateEcriture,
        journal_type: journalType,
        lignes,
      }).unwrap();
      toast.success("Écriture validée et enregistrée au journal.");
      onValidated?.();
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || "Erreur lors de la validation.");
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      style={{
        overlay: { background: "rgba(15,23,42,0.55)", zIndex: 1000 },
        content: {
          maxWidth: 760,
          margin: "auto",
          padding: 0,
          border: "none",
          borderRadius: 14,
          overflow: "hidden",
        },
      }}
    >
      <div style={{ padding: 20, background: "#fff" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: 17, color: "#0f172a" }}>
              <FiCpu size={18} color="#009688" style={{ marginRight: 8 }} />
              Écriture proposée par l&lsquo;OCR
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
              {piece?.titre || ""} : {piece?.partenaire ? `Partenaire : ${piece.partenaire}` : "Partenaire inconnu"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748b" }}
            aria-label="Fermer"
          >
            <FiX size={20} />
          </button>
        </div>

        {isProposing || !proposition ? (
          <div style={{ padding: 30 }}>
            <TableLoader />
            <p style={{ textAlign: "center", color: "#64748b", fontSize: 13, marginTop: 10 }}>
              Analyse de la pièce en cours…
            </p>
          </div>
        ) : (
          <>
            {/* Bandeau confiance + statut */}
            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
              <span style={{
                background: confiance >= 90 ? "#dcfce7" : confiance >= 70 ? "#fef9c3" : "#fee2e2",
                color: confiance >= 90 ? "#166534" : confiance >= 70 ? "#854d0e" : "#991b1b",
                padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              }}>
                Confiance moteur : {confiance}%
              </span>
              <span style={{
                background: "#e0f2f1", color: "#00796b",
                padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              }}>
                Journal : {JOURNAL_LABELS[journalType] || journalType}
              </span>
              {dejaComptabilisee && (
                <span style={{
                  background: "#fef3c7", color: "#92400e",
                  padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                }}>
                  Déjà comptabilisée : la validation remplacera l&lsquo;écriture existante
                </span>
              )}
            </div>

            {/* Avertissements */}
            {avertissements.length > 0 && (
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 10, marginBottom: 14 }}>
                {avertissements.map((a, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", fontSize: 12, color: "#92400e", marginBottom: i < avertissements.length - 1 ? 4 : 0 }}>
                    <FiAlertTriangle size={14} style={{ marginRight: 6, flexShrink: 0 }} />
                    {a}
                  </div>
                ))}
              </div>
            )}

            {/* Libellé + date (modifiables) */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginBottom: 4, display: "block" }}>
                  Libellé de l&lsquo;écriture
                </label>
                <input
                  type="text"
                  value={libelle}
                  onChange={e => setLibelle(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginBottom: 4, display: "block" }}>
                  Date
                </label>
                <input
                  type="date"
                  value={dateEcriture || ""}
                  onChange={e => setDateEcriture(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginBottom: 4, display: "block" }}>
                  Journal
                </label>
                <select
                  value={journalType}
                  onChange={e => setJournalType(e.target.value)}
                  style={inputStyle}
                >
                  {Object.entries(JOURNAL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Lignes débit/crédit */}
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 130px 1fr", background: "#f8fafc", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                <div>Compte</div>
                <div>Sens</div>
                <div style={{ textAlign: "right" }}>Montant</div>
                <div>Libellé ligne</div>
              </div>
              {lignes.map((l, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 130px 1fr", padding: "8px 10px", borderTop: "1px solid #f1f5f9", alignItems: "center", gap: 8 }}>
                  <input
                    type="number"
                    value={l.code_compte}
                    onChange={e => updateLigne(i, "code_compte", Number(e.target.value))}
                    style={inputSm}
                  />
                  <select
                    value={l.sens}
                    onChange={e => updateLigne(i, "sens", e.target.value)}
                    style={inputSm}
                  >
                    <option value="debit">Débit</option>
                    <option value="credit">Crédit</option>
                  </select>
                  <input
                    type="number"
                    value={l.montant}
                    onChange={e => updateLigne(i, "montant", Number(e.target.value))}
                    style={{ ...inputSm, textAlign: "right" }}
                  />
                  <input
                    type="text"
                    value={l.libelle || ""}
                    onChange={e => updateLigne(i, "libelle", e.target.value)}
                    style={inputSm}
                  />
                </div>
              ))}
              {/* Totaux */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 130px 1fr", padding: "8px 10px", borderTop: "2px solid #e2e8f0", background: "#f8fafc", fontSize: 12, fontWeight: 700 }}>
                <div style={{ gridColumn: "1 / 3" }}>Totaux</div>
                <div style={{ textAlign: "right", color: equilibre ? "#00796b" : "#991b1b" }}>
                  D: {fmt(totalDebit)} | C: {fmt(totalCredit)}
                </div>
                <div style={{ display: "flex", alignItems: "center", color: equilibre ? "#00796b" : "#991b1b" }}>
                  {equilibre ? <FiCheckCircle size={14} style={{ marginRight: 4 }} /> : <FiAlertTriangle size={14} style={{ marginRight: 4 }} />}
                  {equilibre ? "Équilibrée" : "Non équilibrée"}
                </div>
              </div>
            </div>

            {/* Rationale */}
            {proposition?.lignes?.[0]?.rationale && (
              <p style={{ fontSize: 11, color: "#64748b", marginBottom: 14, fontStyle: "italic" }}>
                <FiEdit3 size={12} style={{ marginRight: 4 }} />
                Proposition MVP déterministe. La boucle d&lsquo;apprentissage (corrections → mapping) viendra prochainement pour atteindre 95-97% de justesse.
              </p>
            )}

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={onClose}
                style={btnSecondary}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleValider}
                disabled={!equilibre || isValidating}
                style={equilibre && !isValidating ? btnPrimary : { ...btnPrimary, opacity: 0.5, cursor: "not-allowed" }}
              >
                {isValidating ? "Validation…" : "Valider l'écriture"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const inputStyle = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const inputSm = {
  width: "100%",
  padding: "5px 8px",
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box",
};

const btnPrimary = {
  background: "#009688",
  color: "#fff",
  border: "none",
  padding: "8px 16px",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 13,
};

const btnSecondary = {
  background: "#fff",
  color: "#475569",
  border: "1px solid #e2e8f0",
  padding: "8px 16px",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 13,
};

export default EcritureProposeeModal;
